import { useEffect, useId, useMemo, useState, type FC } from "react";

import {
  dateInWords,
  isoFromParts,
  monthNames,
  partsFromIso,
  type DateParts
} from "./intakeModel";

type Props = {
  /** "YYYY-MM-DD" or empty. */
  value: unknown;
  /** The ISO date once all three parts make one, null while incomplete. */
  onChange: (iso: string | null) => void;
  language: string;
  labels: {
    day: string;
    month: string;
    year: string;
    monthPlaceholder: string;
    echo: (words: string) => string;
    invalid: string;
  };
  readOnly?: boolean;
  error?: boolean;
  /** Foreground as "r, g, b", to match the page's colors. */
  fgRgb: string;
  ariaLabel: string;
};

/**
 * Day, month by name and year, read back in words ("You wrote: 14 March
 * 1988") so nobody has to know whether the form is mm/dd or dd/mm.
 */
export const IntakeDateInput: FC<Props> = ({
  value,
  onChange,
  language,
  labels,
  readOnly,
  error,
  fgRgb,
  ariaLabel
}) => {
  const id = useId();
  const [parts, setParts] = useState<DateParts>(() => partsFromIso(value));
  const months = useMemo(() => monthNames(language), [language]);

  // A value set from outside (confirming a prefill, a restored draft) wins
  // over what's half-typed, unless it's the date already shown.
  useEffect(() => {
    const incoming = typeof value === "string" ? value : "";
    if (incoming && incoming !== isoFromParts(parts)) {
      setParts(partsFromIso(incoming));
    }
  }, [value]);

  const update = (patch: Partial<DateParts>) => {
    if (readOnly) return;
    const next = { ...parts, ...patch };
    setParts(next);
    onChange(isoFromParts(next));
  };

  const iso = isoFromParts(parts);
  const words = iso ? dateInWords(iso, language) : null;
  const complete = Boolean(parts.day && parts.month && parts.year.length === 4);
  const muted = `rgba(${fgRgb}, 0.6)`;

  return (
    <div
      className="flex w-full flex-col gap-3"
      role="group"
      aria-label={ariaLabel}
    >
      <div className="flex w-full items-end gap-4 mobile:gap-3">
        <div className="flex w-20 flex-none flex-col gap-1">
          <label
            htmlFor={`${id}-day`}
            className="text-xs"
            style={{ color: muted }}
          >
            {labels.day}
          </label>
          <input
            id={`${id}-day`}
            inputMode="numeric"
            autoComplete="bday-day"
            maxLength={2}
            placeholder="14"
            value={parts.day}
            readOnly={readOnly}
            aria-invalid={error || (complete && !iso) || undefined}
            onChange={(e) => update({ day: e.target.value.replace(/\D/g, "") })}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label
            htmlFor={`${id}-month`}
            className="text-xs"
            style={{ color: muted }}
          >
            {labels.month}
          </label>
          <select
            id={`${id}-month`}
            autoComplete="bday-month"
            value={parts.month}
            disabled={readOnly}
            onChange={(e) => update({ month: e.target.value })}
            className="h-14.5 w-full min-w-0 cursor-pointer appearance-none border-0 border-b border-solid bg-transparent pb-2.5 text-2xl leading-tight outline-none focus:outline-none disabled:cursor-default"
            style={{
              borderColor: `rgba(${fgRgb}, 0.28)`,
              color: parts.month ? `rgb(${fgRgb})` : `rgba(${fgRgb}, 0.36)`
            }}
          >
            <option value="" disabled>
              {labels.monthPlaceholder}
            </option>
            {months.map((name, i) => (
              <option
                key={name}
                value={String(i + 1)}
                className="text-[#111827]"
              >
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex w-28 flex-none flex-col gap-1">
          <label
            htmlFor={`${id}-year`}
            className="text-xs"
            style={{ color: muted }}
          >
            {labels.year}
          </label>
          <input
            id={`${id}-year`}
            inputMode="numeric"
            autoComplete="bday-year"
            maxLength={4}
            placeholder="1988"
            value={parts.year}
            readOnly={readOnly}
            aria-invalid={error || (complete && !iso) || undefined}
            onChange={(e) =>
              update({ year: e.target.value.replace(/\D/g, "") })
            }
          />
        </div>
      </div>
      <p
        className="min-h-5 text-sm"
        style={{ color: muted }}
        aria-live="polite"
      >
        {words ? labels.echo(words) : complete ? labels.invalid : null}
      </p>
    </div>
  );
};
